import { useState } from "react";

import { Undo2 } from "lucide-react";
import PropTypes from "prop-types";
import classnames from "classnames";
import Modal from "@components/ui-components/modal";
import { toastify } from "@helpers/toast";

const PRESETS = ["Wrong amount", "Incorrect UTR / reference", "Paid to wrong beneficiary", "Brand requested a change"];

const RevertDialog = ({ title, description, actionLabel = "Revert", triggerLabel = "Revert", onConfirm, disabled = false }) => {
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
        if (!reason.trim()) {
            toastify({ title: "A reason is required to revert", type: "error" });
            return;
        }
        setSubmitting(true);
        try {
            await onConfirm(reason.trim());
            setReason("");
            setOpen(false);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <button
                type="button"
                disabled={disabled}
                onClick={() => setOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
                <Undo2 className="h-3.5 w-3.5" />
                {triggerLabel}
            </button>

            <Modal
                open={open}
                onClose={() => setOpen(false)}
                title={title}
                subtitle={description}
                footer={(
                    <>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            disabled={submitting}
                            className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={submit}
                            disabled={submitting}
                            className="rounded-lg bg-danger px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-danger/90 disabled:cursor-not-allowed disabled:opacity-70">
                            {actionLabel}
                        </button>
                    </>
                )}>
                <div>
                    <label className="mb-1.5 block text-sm font-medium text-gray-700">Reason for reversal</label>
                    <textarea
                        rows={3}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Explain why this step is being reverted — it is written to the audit log."
                        className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />

                    <div className="mt-3 flex flex-wrap gap-2">
                        {PRESETS.map((preset) => (
                            <button
                                key={preset}
                                type="button"
                                onClick={() => setReason(preset)}
                                className={classnames(
                                    "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                                    reason === preset
                                        ? "border-primary bg-primary/5 text-primary"
                                        : "border-gray-300 text-gray-600 hover:bg-gray-50"
                                )}>
                                {preset}
                            </button>
                        ))}
                    </div>
                </div>
            </Modal>
        </>
    );
};

RevertDialog.propTypes = {
    title       : PropTypes.string.isRequired,
    description : PropTypes.node.isRequired,
    actionLabel : PropTypes.string,
    triggerLabel: PropTypes.string,
    onConfirm   : PropTypes.func.isRequired,
    disabled    : PropTypes.bool
};

export default RevertDialog;
